import { useEffect, useState } from "react";
import type { ZonedDateTime } from "@internationalized/date";
import { fromDate, today } from "@internationalized/date";
import { Clock } from "lucide-react";

import { FormattedDate } from "@workspace/ui/components/formatted-date";

interface DigitalClockProps {
	showSeconds?: boolean;
	showDate?: boolean;
	className?: string;
	timeZone: string;
}

// Extract the time/update logic into useZonedTime and update both components to use it.
function useZonedTime(timeZone: string, tick = 1000) {
	const [time, setTime] = useState<ZonedDateTime>(
		fromDate(new Date(), timeZone),
	);

	useEffect(() => {
		const timer = setInterval(() => {
			setTime(fromDate(new Date(), timeZone));
		}, tick);

		return () => {
			clearInterval(timer);
		};
	}, [timeZone, tick]);

	return time;
}

function ClockDisplay({ showSeconds = true, timeZone }: DigitalClockProps) {
	const time = useZonedTime(timeZone);

	return (
		<FormattedDate
			date={time.toDate()}
			options={{
				hour: "numeric",
				minute: "numeric",
				second: showSeconds ? "numeric" : undefined,
				hour12: true,
			}}
			timeZone={timeZone}
		/>
	);
}

function DateDisplay({ timeZone }: { timeZone: string }) {
	return <FormattedDate date={today(timeZone).toDate(timeZone)} />;
}

export function DigitalClock({
	showSeconds = true,
	showDate = false,
	className = "",
	timeZone,
}: DigitalClockProps) {
	return (
		<div className={`flex items-center ${className}`}>
			<Clock className="h-4 w-4 mr-2" />
			<div className="flex flex-col">
				<span className="font-mono font-medium">
					<ClockDisplay showSeconds={showSeconds} timeZone={timeZone} />
				</span>
				{showDate && (
					<span className="text-xs text-muted-foreground">
						<DateDisplay timeZone={timeZone} />
					</span>
				)}
			</div>
		</div>
	);
}

export function LargeDigitalClock({
	timeZone = "UTC",
	showSeconds = true,
	showDate = true,
}: DigitalClockProps) {
	const time = useZonedTime(timeZone);

	const formatTime = () => {
		let hours = time.hour;
		const ampm = hours >= 12 ? "PM" : "AM";

		hours = hours % 12;
		hours = hours ? hours : 12; // the hour '0' should be '12'

		return (
			<>
				<span className="font-mono text-6xl font-bold tabular-nums md:text-8xl">
					{hours.toString().padStart(2, "0")}
					<span className="animate-pulse">:</span>
					{time.minute.toString().padStart(2, "0")}
					{showSeconds && (
						<>
							<span className="animate-pulse">:</span>
							{time.second.toString().padStart(2, "0")}
						</>
					)}
				</span>
				<span className="ml-2 text-2xl md:text-3xl">{ampm}</span>
			</>
		);
	};

	return (
		<div className="flex flex-col items-center">
			<div className="flex items-end justify-center">{formatTime()}</div>
			{showDate && (
				<>
					<div className="mt-2 text-xl font-medium md:text-2xl">
						<FormattedDate
							date={time.toDate()}
							options={{
								weekday: "long",
								year: "numeric",
								month: "long",
								day: "numeric",
								timeZone,
							}}
						/>
					</div>
				</>
			)}
		</div>
	);
}
